import { observable, action, runInAction, computed } from "mobx";

import PageStore from "app/PageStore";
import { sanitize } from "lib/formatter";

import RfeService from "./RfeService";
import { CaseType, Pdf, RfeImage, Block } from "./models";
import RfeUploadForm from "./commands/RfeUploadForm";
import RfeEditForm from "./commands/RfeEditForm";

export default class RfeStore {
  @observable public loading = false;
  @observable public categories: string[][] = [];
  @observable public caseTypes: CaseType[] = [];
  @observable public entityTypes: string[] = [];
  @observable public pdfs: Array<{ key: string; list: Pdf[] }> = [];
  @observable public images: RfeImage[] = [];
  @observable public blocks: Block[] = [];
  @observable public imageIds: number[] = [];
  @observable public blockIds: number[] = [];

  @observable public uploadForm: RfeUploadForm;
  @observable public editForm: RfeEditForm;

  @observable private selectedPdfId?: number;
  @observable private selectedImageId?: number;
  @observable private selectedBlockId?: number;

  private pageStore: PageStore;
  private rfeService: RfeService;

  constructor(pageStore: PageStore, rfeService: RfeService) {
    this.pageStore = pageStore;
    this.rfeService = rfeService;
    this.reset();
  }

  @computed
  get pdfId(): number | undefined {
    return this.selectedPdfId;
  }

  @computed
  get image() {
    return this.selectedImageId
      ? this.images.find((x) => x.id === this.selectedImageId)
      : undefined;
  }

  @computed
  get imageId(): number | undefined {
    return this.selectedImageId;
  }

  @computed
  get blockId(): number | undefined {
    return this.selectedBlockId;
  }

  @computed
  get prevImageId(): number | undefined {
    const currentIndex = this.imageIds.indexOf(this.imageId!);
    const prevImage = this.images[currentIndex - 1];
    return prevImage ? Number(prevImage.id) : undefined;
  }

  @computed
  get nextImageId(): number | undefined {
    const currentIndex = this.imageIds.indexOf(this.imageId!);
    const nextImage = this.images[currentIndex + 1];
    return nextImage ? Number(nextImage.id) : undefined;
  }

  @computed
  get isFirstImage(): boolean {
    return !!this.selectedImageId && this.selectedImageId === this.images[0].id;
  }

  @computed
  get isLastImage(): boolean {
    return (
      !!this.selectedImageId &&
      this.selectedImageId === this.images[this.images.length - 1].id
    );
  }

  @computed
  get firstBlockId(): number | undefined {
    return this.blocks.length > 0 ? Number(this.blocks[0].id) : undefined;
  }

  @computed
  get lastBlockId(): number | undefined {
    return this.blocks.length > 0
      ? Number(this.blocks[this.blocks.length - 1].id)
      : undefined;
  }

  @computed
  get isFirstBlock(): boolean {
    return !!this.selectedBlockId && this.selectedBlockId === this.firstBlockId;
  }

  @computed
  get isLastBlock(): boolean {
    return !!this.selectedBlockId && this.selectedBlockId === this.lastBlockId;
  }

  @computed
  get blockLayout(): Block | undefined {
    return this.selectedBlockId
      ? this.blocks.find((x) => x.id === this.selectedBlockId)
      : undefined;
  }

  @action.bound
  public async listPdfs() {
    const { openSpinner, openToast, closeSpinner } = this.pageStore;
    openSpinner();
    try {
      const res = await this.rfeService.findPdfs();
      if (!res) {
        throw res;
      }
      runInAction(() => {
        this.pdfs = res;
      });
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      closeSpinner();
    }
  }

  @action.bound
  public async listCaseTypes() {
    const { openSpinner, openToast, closeSpinner } = this.pageStore;
    openSpinner();
    try {
      const res = await this.rfeService.findCaseTypes();
      if (!res) {
        throw res;
      }
      runInAction(() => {
        this.caseTypes = res;
        if (res.length === 1) {
          const { base, caseName } = res[0];
          this.editForm.update({
            base,
            caseName,
          });
          this.uploadForm.update({
            base,
            caseName,
          });
        }
      });
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      closeSpinner();
    }
  }

  @action.bound
  public async listCategories() {
    const { openSpinner, openToast, closeSpinner } = this.pageStore;
    openSpinner();
    try {
      const res = await this.rfeService.findCategories(this.editForm);
      if (!res) {
        throw new Error("Find category is failed.");
      }
      runInAction(() => {
        this.categories = res;
      });
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      closeSpinner();
    }
  }

  @action.bound
  public async listEntityTypes() {
    const { openSpinner, openToast, closeSpinner } = this.pageStore;
    openSpinner();
    try {
      const res = await this.rfeService.findEntityTypes();
      if (!res) {
        throw res;
      }
      runInAction(() => {
        this.entityTypes = res;
      });
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      closeSpinner();
    }
  }

  @action.bound
  public async listImages() {
    const { openSpinner, openToast, closeSpinner } = this.pageStore;
    openSpinner();
    try {
      if (!this.selectedPdfId) {
        throw new Error("Not valid PDF id");
      }
      const res = await this.rfeService.findImages(this.selectedPdfId);
      if (!res) {
        throw res;
      }
      runInAction(() => {
        this.images = res.sort(this.sortFiles);
        this.imageIds = this.images.map((x) => Number(x.id));
      });
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      closeSpinner();
    }
  }

  @action.bound
  public async listBlocks(imageId: number) {
    const { openSpinner, openToast, closeSpinner } = this.pageStore;
    openSpinner();
    try {
      if (!imageId) {
        throw new Error("Not valid image id");
      }
      const res = await this.rfeService.findBlocks(imageId);
      if (!res) {
        throw res;
      }
      runInAction(() => {
        this.blocks = res.sort((a, b) => Number(a.id) - Number(b.id));
        this.blockIds = this.blocks.map((x) => Number(x.id));
      });
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      closeSpinner();
    }
  }

  @action.bound
  public async loadEditPage(params: {
    base?: string;
    caseName?: string;
    pdfId?: number;
    imageId?: number;
  }) {
    const form = this.editForm;
    const { base, caseName, pdfId, imageId } = params;
    if (!!pdfId && !!base && !!caseName) {
      return runInAction(async () => {
        this.selectedPdfId = pdfId;
        form.update({
          base,
          caseName,
        });
        await this.listCategories();
        await this.listImages();
        if (imageId) {
          this.selectedImageId = imageId;
          await this.listBlocks(imageId);
        }
      });
    }
  }

  @action.bound
  public selectPdf(pdfId: number) {
    runInAction(() => {
      this.selectedPdfId = pdfId;
    });
  }

  @action.bound
  public selectImage(imageId: number) {
    runInAction(() => {
      this.selectedImageId = imageId;
    });
  }

  @action.bound
  public selectBlock(blockId: number) {
    runInAction(() => {
      this.selectedBlockId = blockId;
    });
  }

  @action.bound
  public async filePdfUpload({ file: fileSource, onSuccess }: any) {
    const { openSpinner, openToast, closeSpinner } = this.pageStore;
    const form = this.uploadForm;
    const file = new File([fileSource], sanitize(fileSource.name));
    openSpinner();
    form.update({ file });
    try {
      const uploadId = await this.rfeService.checkPdfDuplicated(file);
      if (uploadId) {
        this.pageStore.openAlert({
          title: "Same File is already exist",
          message: "Do you want upload again?",
          onCancel: () => {
            runInAction(() => {
              onSuccess("done", file);
              this.selectedPdfId = uploadId;
            });
          },
          onConfirm: this.pdfUpload,
        });
        return;
      }
      await this.pdfUpload();
      onSuccess("done", file);
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      closeSpinner();
    }
  }

  @action.bound
  public async updateImage(imageId: number) {
    const { openSpinner, openToast, closeSpinner } = this.pageStore;
    openSpinner();
    try {
      const res = await this.rfeService.updateImage(imageId);
      if (!res) {
        throw res;
      }
      runInAction(() => {
        const image = this.images.find((x: RfeImage) => x.id === imageId);
        image!.setComplete();
      });
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      closeSpinner();
    }
  }

  @action.bound
  public reset() {
    this.loading = false;
    this.uploadForm = new RfeUploadForm();
    this.editForm = new RfeEditForm();
  }

  @action.bound
  private async pdfUpload() {
    try {
      const res = await this.rfeService.filePdfUpload(this.uploadForm);
      if (!res) {
        throw res;
      }
      runInAction(() => {
        this.selectedPdfId = res.id;
      });
    } catch (e) {
      throw e;
    }
  }

  private sortFiles(a: any, b: any) {
    if (a.file < b.file) {
      return -1;
    }
    if (a.file > b.file) {
      return 1;
    }
    return 0;
  }
}
